import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { useLoadDebtorAiSummary } from 'app/Private/Clients/Detail/InfoTab/components/AiSummaryPanel/hooks/use-load-debtor-ai-summary';
import type {
  ActionType,
  InvoiceAction,
  UnhandledInvoice,
} from 'modules/AtRisk/api/use-load-unhandled-client';
import { prop, sortBy } from 'remeda';
import type { ActionButtonDefinition } from './action-button-definition';
import {
  type ActionButtonGroup,
  groupActionButtons,
  type TypedActionButton,
} from './action-button-groups';
import {
  useAdditionalReminder,
  useBailiff,
  useDebtCollector,
  useDirectContact,
  useFormalNotice,
  useLawyer,
  useMarkAsDisputed,
  useMarkAsPaid,
  useMarkAsUnrecoverable,
  usePaymentPlan,
} from './actions/';
import { usePause } from './actions/pause';

export type { ActionButtonDefinition } from './action-button-definition';
export type { ActionButtonGroup } from './action-button-groups';

function getAiRecommendedActionType(
  recommendedActions: Array<InvoiceAction> | undefined,
): ActionType | null {
  if (!recommendedActions?.length) return null;
  return sortBy(recommendedActions, [prop('priority'), 'desc'])[0].type;
}

export function useBuildActionButtons(
  actions: Array<InvoiceAction>,
  invoices: Array<UnhandledInvoice>,
): Array<ActionButtonGroup> {
  const additionalReminderFactory = useAdditionalReminder();
  const bailiffFactory = useBailiff();
  const debtCollectorFactory = useDebtCollector();
  const directContactFactory = useDirectContact();
  const formalNoticeFactory = useFormalNotice();
  const lawyerFactory = useLawyer();
  const markAsDisputedFactory = useMarkAsDisputed();
  const markAsPaidFactory = useMarkAsPaid();
  const markAsUnrecoverableFactory = useMarkAsUnrecoverable();
  const paymentPlanFactory = usePaymentPlan();
  const pauseFactory = usePause();

  const debtor = useDebtor();
  const { aiSummary } = useLoadDebtorAiSummary({ id: String(debtor.id) });

  // Move constant out
  const actionButtonBuilders: Record<
    ActionType,
    (action: InvoiceAction, invoices: Array<UnhandledInvoice>) => ActionButtonDefinition
  > = {
    direct_contact: directContactFactory,
    payment_plan: paymentPlanFactory,
    formal_notice: formalNoticeFactory,
    additional_reminder: additionalReminderFactory,
    mark_as_unrecoverable: markAsUnrecoverableFactory,
    mark_as_paid: markAsPaidFactory,
    mark_as_disputed: markAsDisputedFactory,
    lawyer: lawyerFactory,
    bailiff: bailiffFactory,
    debt_collection: debtCollectorFactory,
  };

  const buttons: Array<TypedActionButton> = [
    ...actions.map(action => ({
      type: action.type,
      definition: actionButtonBuilders[action.type](action, invoices),
    })),
    { type: 'pause' as const, definition: pauseFactory(invoices) },
  ];

  const aiRecommendedActionType = aiSummary?.isOutdated
    ? null
    : getAiRecommendedActionType(aiSummary?.summary?.recommendedActions);

  const recommendedButtons = buttons.map(button =>
    button.type === aiRecommendedActionType
      ? { ...button, definition: { ...button.definition, recommended: 'AI' as const } }
      : button,
  );

  return groupActionButtons(recommendedButtons);
}
